import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import ProductCard from "../components/ProductCard";
import TrustStrip from "../components/TrustStrip";
import { Reveal } from "../components/Reveal";
import { CATEGORIES } from "../data/catalog";
import { fetchCatalogue } from "../lib/store";

const Shop = () => {
  const [params, setParams] = useSearchParams();
  const [sort, setSort] = useState("featured");
  const [products, setProducts] = useState([]);
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const filter = params.get("cat") || "All";

  useEffect(() => {
    if (!CATEGORIES.includes(filter)) setParams({}, { replace: true });
  }, [filter, setParams]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { products, sets } = await fetchCatalogue();
        if (!alive) return;
        setProducts(products);
        setSets(sets);
      } catch (e) {
        console.error("Catalogue load failed:", e);
        if (alive) setErr("We couldn't load the shop just now.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const list = useMemo(() => {
    let l;
    if (filter === "All") l = [...products];
    else if (filter === "Sets & Gifting") l = [...sets];
    else l = products.filter((p) => p.category === filter);
    if (sort === "low") l.sort((a, b) => a.price - b.price);
    if (sort === "high") l.sort((a, b) => b.price - a.price);
    if (sort === "new") l.reverse();
    return l;
  }, [filter, sort, products, sets]);

  return (
    <div className="pt-36 md:pt-44" data-testid="shop-page">
      <header className="mx-auto max-w-7xl px-4 md:px-8">
        <Reveal>
          <p className="eyebrow">The shop</p>
          <h1 className="mt-4 font-serif text-5xl font-light leading-tight md:text-6xl">
            Ten products. <span className="italic text-sage-deep">Made by a doctor.</span>
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-ink/70">
            Hair, skin and body care, formulated by Dr. Shibina Shemim and made in our own licensed
            facility in Thiruvananthapuram.
          </p>
          <p className="mt-3 max-w-xl text-sm italic leading-relaxed text-ink/50">
            Most Ayurvedic products have never met an Ayurvedic doctor. These ones were made by one.
          </p>
        </Reveal>
      </header>

      <section className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
        <div className="flex flex-wrap items-center justify-between gap-5 border-y hairline py-5">
          <div className="flex flex-wrap gap-2" data-testid="shop-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setParams(c === "All" ? {} : { cat: c })}
                data-testid={`filter-${c.toLowerCase().replace(/\s|&/g, "-")}`}
                className={`rounded-full px-5 py-2.5 text-[12px] uppercase tracking-[0.16em] transition-colors duration-300 ${
                  filter === c ? "bg-ink text-cream" : "border hairline text-ink/60 hover:text-ink"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-3 text-[12px] uppercase tracking-[0.16em] text-ink/60">
            Sort
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="sort-select"
              className="cursor-pointer border hairline bg-transparent px-4 py-2.5 text-[12px] uppercase tracking-[0.16em] text-ink"
            >
              <option value="featured">Featured</option>
              <option value="low">Price, low to high</option>
              <option value="high">Price, high to low</option>
              <option value="new">New</option>
            </select>
          </label>
        </div>

        {loading && (
          <div className="mt-12 grid grid-cols-2 gap-x-4 gap-y-12 md:gap-x-6 lg:grid-cols-4" data-testid="shop-loading">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[3/4] w-full bg-ink/5" />
                <div className="mt-4 h-3 w-3/4 bg-ink/5" />
                <div className="mt-2 h-3 w-1/3 bg-ink/5" />
              </div>
            ))}
          </div>
        )}

        {!loading && err && (
          <p className="mt-16 text-center text-sm text-ink/60" data-testid="shop-error">
            {err} Please refresh, or write to us and we'll help.
          </p>
        )}

        {!loading && !err && list.length === 0 && (
          <p className="mt-16 text-center text-sm text-ink/60" data-testid="shop-empty">
            Nothing here just yet.
          </p>
        )}

        {!loading && !err && list.length > 0 && (
          <motion.div layout className="mt-12 grid grid-cols-2 gap-x-4 gap-y-12 md:gap-x-6 lg:grid-cols-4" data-testid="shop-grid">
            <AnimatePresence mode="popLayout">
              {list.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </section>

      <TrustStrip />
    </div>
  );
};

export default Shop;
